'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { Send, MessageCircle, ChevronDown, Navigation } from 'lucide-react';

interface ChatMessage {
  id: string;
  booking_id: string;
  sender_role: 'driver' | 'passenger';
  message: string;
  is_read: boolean;
  created_at: string;
}

interface RideChatProps {
  bookingId: string;
  myRole: 'driver' | 'passenger';
  pickupAddress: string;
  dropoffAddress: string;
  otherPartyName: string;
  isActiveRide: boolean;
}

export default function RideChat({ bookingId, myRole, pickupAddress, dropoffAddress, otherPartyName, isActiveRide }: RideChatProps) {
  const supabase = createClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchMessages = useCallback(async () => {
    const { data } = await supabase
      .from('ride_chat_messages')
      .select('*')
      .eq('booking_id', bookingId)
      .order('created_at', { ascending: true });
    setMessages(data || []);
    // Count unread messages from other party
    const unread = (data || []).filter(m => m.sender_role !== myRole && !m.is_read).length;
    setUnreadCount(unread);
  }, [bookingId, myRole]);

  const markMessagesRead = useCallback(async () => {
    await supabase
      .from('ride_chat_messages')
      .update({ is_read: true })
      .eq('booking_id', bookingId)
      .neq('sender_role', myRole)
      .eq('is_read', false);
    setUnreadCount(0);
  }, [bookingId, myRole]);

  useEffect(() => {
    if (!bookingId) return;
    fetchMessages();

    // Real-time subscription for new messages
    const channel = supabase
      .channel(`ride-chat-${bookingId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'ride_chat_messages',
        filter: `booking_id=eq.${bookingId}`,
      }, (payload) => {
        const msg = payload.new as ChatMessage;
        setMessages(prev => {
          if (prev.find(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        if (msg.sender_role !== myRole) {
          if (isOpen) {
            markMessagesRead();
          } else {
            setUnreadCount(prev => prev + 1);
          }
        }
      })
      // Typing indicator via broadcast
      .on('broadcast', { event: 'typing' }, (payload) => {
        if (payload.payload?.role !== myRole) {
          setOtherTyping(true);
          setTimeout(() => setOtherTyping(false), 3000);
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [bookingId, isOpen]);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      markMessagesRead();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, messages.length]);

  const handleTyping = () => {
    if (!isTyping) {
      setIsTyping(true);
      // Broadcast typing indicator
      supabase.channel(`ride-chat-${bookingId}`).send({
        type: 'broadcast',
        event: 'typing',
        payload: { role: myRole },
      });
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 2000);
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text || sending) return;

    setSending(true);
    setNewMessage('');
    try {
      await supabase.from('ride_chat_messages').insert({
        booking_id: bookingId,
        sender_role: myRole,
        message: text,
        is_read: false,
      });
    } catch {
      setNewMessage(text); // restore on error
    } finally {
      setSending(false);
    }
  };

  if (!isActiveRide) return null;

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-105 active:scale-95"
          style={{ background: 'var(--primary)' }}
          aria-label="Open ride chat"
        >
          <MessageCircle className="w-6 h-6" style={{ color: 'var(--primary-foreground)' }} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'var(--danger)' }}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div
          className="fixed bottom-0 right-0 z-50 flex flex-col shadow-2xl"
          style={{
            width: '100%',
            maxWidth: '420px',
            height: '520px',
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: '16px 16px 0 0',
          }}
        >
          {/* Chat Header — shows ride context */}
          <div
            className="flex-shrink-0 px-4 py-3 border-b"
            style={{ background: 'var(--card)', borderColor: 'var(--border)', borderRadius: '16px 16px 0 0' }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'rgba(245,158,11,0.15)', color: 'var(--primary)' }}>
                  {otherPartyName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{otherPartyName}</p>
                  <p className="text-xs" style={{ color: 'var(--success)' }}>● In-ride chat</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                aria-label="Close chat"
              >
                <ChevronDown className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
              </button>
            </div>
            {/* Ride context strip */}
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs" style={{ background: 'var(--muted)' }}>
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: 'var(--success)' }} />
                <span className="truncate" style={{ color: 'var(--muted-foreground)' }}>{pickupAddress}</span>
              </div>
              <Navigation className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }} />
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: 'var(--danger)' }} />
                <span className="truncate" style={{ color: 'var(--muted-foreground)' }}>{dropoffAddress}</span>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 scrollbar-thin">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full gap-2">
                <MessageCircle className="w-10 h-10" style={{ color: 'var(--muted-foreground)', opacity: 0.4 }} />
                <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
                  Chat with your {myRole === 'passenger' ? 'driver' : 'passenger'} during the ride
                </p>
              </div>
            )}
            {messages.map((msg) => {
              const isMe = msg.sender_role === myRole;
              return (
                <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className="max-w-[75%] px-3 py-2 rounded-2xl text-sm"
                    style={{
                      background: isMe ? 'var(--primary)' : 'var(--muted)',
                      color: isMe ? 'var(--primary-foreground)' : 'var(--foreground)',
                      borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    }}
                  >
                    <p className="leading-relaxed">{msg.message}</p>
                    <p
                      className="text-xs mt-1 text-right"
                      style={{ opacity: 0.65 }}
                    >
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
            {/* Typing indicator */}
            {otherTyping && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-2xl" style={{ background: 'var(--muted)', borderRadius: '16px 16px 16px 4px' }}>
                  <div className="flex items-center gap-1">
                    {[0, 1, 2].map(i => (
                      <div
                        key={i}
                        className="w-1.5 h-1.5 rounded-full animate-bounce"
                        style={{ background: 'var(--muted-foreground)', animationDelay: `${i * 0.15}s` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form
            onSubmit={sendMessage}
            className="flex-shrink-0 px-3 py-3 border-t flex items-center gap-2"
            style={{ borderColor: 'var(--border)' }}
          >
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => { setNewMessage(e.target.value); handleTyping(); }}
              placeholder="Type a message..."
              className="flex-1 px-3 py-2 rounded-xl text-sm outline-none"
              style={{
                background: 'var(--muted)',
                color: 'var(--foreground)',
                border: '1px solid var(--border)',
              }}
              maxLength={500}
              disabled={sending}
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-opacity disabled:opacity-40"
              style={{ background: 'var(--primary)' }}
              aria-label="Send message"
            >
              <Send className="w-4 h-4" style={{ color: 'var(--primary-foreground)' }} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
